import React, { useEffect, useState, Fragment } from "react";
import { useFormikContext } from "formik";
import { getFieldPath, formatValidationError } from "utils/form";
import startCase from "lodash/snakeCase";
import LoadingSpinner from "components/LoadingSpinner";
import { useDebounce, useIsMounted } from "utils/hooks";
import get from "lodash/get";
import set from "lodash/set";
import { AUTO_ATTRS } from "components/Form/fields/constants";

/**
 * onChange - set the input value locally and debounce update events to prevent too many renders
 * optionally use a sync to be triggered on set (e.g syncing product names)
 * @param {Object} props
 * @param {String} props.fieldKey - the key of the related model (e.g. name for Product.name)
 * @param {String} props.rootPath - the root of the form state path (e.g. manifest.data.content.[UUID].data.products[2])
 * @param {String} props.name - the key of the field path to set in the form (e.g title)
 * @param {String} props.label - label for the field
 * @param {Function} props.onSet - callback to be triggered on set value (i.e. sync Stripe product name)
 * @param {Function} props.innerRef - ref to be passed to the input
 */
const FieldController = ({
  fieldKey,
  rootPath,
  name,
  hideLabel,
  label,
  onSet,
  validationSchema,
  innerRef
}) => {
  const {
    values,
    setFieldValue,
    setFieldTouched,
    setErrors,
    errors
  } = useFormikContext();
  const isMounted = useIsMounted();
  const [value, setValue] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fieldName = getFieldPath(rootPath, name);
  const error = get(errors, fieldName);

  useEffect(() => {
    if (typeof value === "string" && !error) {
      /**
       * If there is an onSet trigger
       * - set a loader
       * - make the request
       * - update the form state
       * - unset the loader
       */
      if (onSet) {
        if (isMounted.current) {
          setIsSubmitting(true);
        }
        onSet({ [fieldKey]: value }, () => {
          /**
           * Set the updated value in form state
           * - will trigger a re-computation
           */
          setFieldValue(fieldName, value);
          setFieldTouched(fieldName, true);
          /**
           * Clear the local value state
           */
          setValue(null);
          if (isMounted.current) {
            setIsSubmitting(false);
          }
        });
      } else {
        setFieldValue(fieldName, value);
        setFieldTouched(fieldName, true);
        setValue(null);
      }
    }
  }, [useDebounce(value, 500)]);

  const inputProps = {
    name: fieldName,
    className:
      "f5 border-box input-reset ba pa2 db w-100 br2 lh-copy b--black-20",
    ...AUTO_ATTRS,
    onChange: (evt) => {
      if (!isSubmitting) {
        setValue(evt.currentTarget.value);
      }
    }
  };
  if (innerRef) {
    inputProps.ref = innerRef;
  }

  const valueToValidate =
    typeof value === "string" ? value : get(values, fieldName);
  inputProps.value = valueToValidate;

  useEffect(() => {
    if (validationSchema) {
      // WHERES THE ERROR OBJECT??
      validationSchema
        .validate({ [name]: valueToValidate })
        .then(() => {
          if (error) {
            set(errors, fieldName, null);
            setErrors(errors);
          }
        })
        .catch((err) => {
          set(errors, fieldName, {
            message: err.message,
            path: err.path
          });
          setErrors(errors);
        });
    }
  }, [valueToValidate]);

  return (
    <Fragment>
      {!hideLabel && (
        <label className="f6 mb2 dib fw6">{label || startCase(name)}</label>
      )}
      <div className="flex flex-row relative w-100">
        <input {...inputProps} />
        {isSubmitting && (
          <div className="absolute top-0 right-0 bottom-0">
            <div className="flex w-100 h-100 items-center justify-center pr2 pb1">
              <LoadingSpinner
                customClasses={{
                  ring: "blue"
                }}
              />
            </div>
          </div>
        )}
      </div>
      {error && (
        <div className="f6 w-100 self-center pt2 tl color-error">
          {formatValidationError(error.message)}
        </div>
      )}
    </Fragment>
  );
};

export default FieldController;
